import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Check, Share2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const SNIPPETS = [["img", "Image (fond sombre)"], ["img_light", "Image (fond clair)"], ["link", "Image cliquable vers vos avis"], ["url", "Lien direct vers vos avis"]];

// Phase 65 — Badge avis partageable : aperçu SVG + extraits HTML à coller sur un site, une signature ou un réseau
export function ReviewBadgeShare() {
  const [badge, setBadge] = useState(null);
  const [copied, setCopied] = useState("");
  const [theme, setTheme] = useState("dark");
  useEffect(() => { api.get("/chef/reviews/badge").then((r) => setBadge(r.data)).catch(() => setBadge({ available: false, reason: "Badge indisponible" })); }, []);

  const copy = async (k) => {
    try { await navigator.clipboard.writeText(badge.snippets[k]); setCopied(k); toast.success("Copié dans le presse-papiers"); setTimeout(() => setCopied(""), 1500); }
    catch { toast.error("Copie impossible — sélectionnez le texte manuellement"); }
  };

  if (!badge) return null;
  return (
    <div className="bg-white border border-[#DEE2E6] p-5" data-testid="avis-badge">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] flex items-center gap-1"><Share2 className="h-3.5 w-3.5" /> Partager mon badge</div>
          <p className="text-xs text-[#495057] mt-1 max-w-xl">Affichez votre note moyenne et le nombre d'avis vérifiés sur votre site, votre signature email ou vos réseaux. Le badge se met à jour automatiquement à chaque avis publié.</p>
        </div>
        {badge.available && <div className="flex gap-1" data-testid="avis-badge-theme">{[["dark", "Sombre"], ["light", "Clair"]].map(([k, l]) => <button key={k} onClick={() => setTheme(k)} data-testid={`avis-badge-theme-${k}`} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${theme === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>{l}</button>)}</div>}
      </div>

      {!badge.available ? (
        <div className="mt-4 border border-[#DEE2E6] bg-[#F8F9FA] p-4 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="avis-badge-unavailable">
          <span>{badge.reason}</span>
          <Link to="/chef/minisite" className="inline-flex items-center gap-1 text-xs font-bold text-[#002FA7] hover:underline" data-testid="avis-badge-minisite-link">Ouvrir le mini-site <ExternalLink className="h-3 w-3" /></Link>
        </div>
      ) : (
        <div className="mt-4 grid lg:grid-cols-[320px_1fr] gap-5">
          <div className={`p-5 flex items-center justify-center ${theme === "dark" ? "bg-[#F1F3F5]" : "bg-[#0A0A0A]"}`} data-testid="avis-badge-preview">
            <img src={theme === "dark" ? badge.urls.svg_dark : badge.urls.svg_light} alt="Aperçu du badge avis" width="300" height="92" data-testid="avis-badge-img" />
          </div>
          <div className="space-y-2">
            {!badge.has_reviews && <div className="text-xs text-[#D97706] font-semibold" data-testid="avis-badge-no-reviews">Aucun avis publié pour l'instant : le badge affiche « Avis vérifiés — bientôt » jusqu'à votre première publication.</div>}
            {SNIPPETS.map(([k, l]) => (
              <div key={k} className="border border-[#DEE2E6]" data-testid={`avis-badge-snippet-${k}`}>
                <div className="flex items-center justify-between px-3 py-1.5 bg-[#F8F9FA] border-b border-[#DEE2E6]">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#495057]">{l}</span>
                  <Button size="sm" variant="ghost" onClick={() => copy(k)} data-testid={`avis-badge-copy-${k}`} className="h-7 px-2 text-xs">{copied === k ? <Check className="h-3.5 w-3.5 mr-1 text-[#059669]" /> : <Copy className="h-3.5 w-3.5 mr-1" />}{copied === k ? "Copié" : "Copier"}</Button>
                </div>
                <code className="block px-3 py-2 text-[11px] font-mono break-all text-[#212529]">{badge.snippets[k]}</code>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
